"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Loader2, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { RecipeTile } from "@/components/shared/RecipeTile";
import { trackCalendar } from "@/lib/analytics";
import { easeSmooth, fadeVariants, springSnappy } from "@/lib/animations";
import type { CalendarEntryWithRecipe } from "@/lib/calendar/server";
import { addDays, parseIsoDate, toIsoDate } from "@/lib/calendar/week";
import type { RecipeTileData } from "@/lib/recipes/types";
import { cn } from "@/lib/utils";

interface CalendarWeekProps {
  locale: string;
  weekStartIso: string;
  todayIso: string;
  entries: CalendarEntryWithRecipe[];
  isNavigating?: boolean;
  onOpenPlanSheet: (cookOn: string) => void;
}

function toTileData(recipe: CalendarEntryWithRecipe["recipe"]): RecipeTileData {
  return {
    ...recipe,
    publishedAt: recipe.publishedAt ? String(recipe.publishedAt) : null,
    source: null,
    tags: [],
    userId: "",
  };
}

function MealCard({
  entry,
  onRemove,
  unplanLabel,
  reducedMotion,
}: {
  entry: CalendarEntryWithRecipe;
  onRemove: () => void;
  unplanLabel: string;
  reducedMotion: boolean;
}) {
  return (
    <motion.div
      layout={!reducedMotion}
      initial={reducedMotion ? false : { opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
      transition={springSnappy}
      className="group relative w-[calc(50%-6px)] sm:w-36 lg:w-40"
    >
      <RecipeTile recipe={toTileData(entry.recipe)} aspectRatio={1} compact sizes="160px" />
      <button
        type="button"
        onClick={onRemove}
        aria-label={unplanLabel}
        className="absolute end-1.5 top-1.5 z-10 flex size-6 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </motion.div>
  );
}

export function CalendarWeek({
  locale,
  weekStartIso,
  todayIso,
  entries,
  isNavigating = false,
  onOpenPlanSheet,
}: CalendarWeekProps) {
  const t = useTranslations("calendar");
  const queryClient = useQueryClient();
  const prefersReduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const weekStart = parseIsoDate(weekStartIso) ?? new Date();

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });

  type CacheData = { entries: CalendarEntryWithRecipe[]; weekStart: string; weekEnd: string };

  const removeEntry = (entryId: string, cookOn: string) => {
    trackCalendar("meal_removed", { cookOn });

    const prev = queryClient.getQueriesData<CacheData>({ queryKey: ["calendar"] });

    queryClient.setQueriesData<CacheData>({ queryKey: ["calendar"] }, (old) =>
      old ? { ...old, entries: old.entries.filter((e) => e.id !== entryId) } : old
    );

    fetch(`/api/calendar/${entryId}`, { method: "DELETE" })
      .then((res) => {
        if (!res.ok) {
          for (const [key, data] of prev) queryClient.setQueryData(key, data);
          toast.error(t("removeError"));
        } else {
          queryClient.invalidateQueries({ queryKey: ["calendar"] });
          queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        }
      })
      .catch(() => {
        for (const [key, data] of prev) queryClient.setQueryData(key, data);
        toast.error(t("removeError"));
      });
  };

  const entriesByDay = new Map<string, CalendarEntryWithRecipe[]>();
  for (const e of entries) {
    const list = entriesByDay.get(e.cookOn) ?? [];
    list.push(e);
    entriesByDay.set(e.cookOn, list);
  }

  const loading = isNavigating;

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            variants={prefersReduced ? undefined : fadeVariants}
            initial={mounted ? "hidden" : false}
            animate="visible"
            exit="exit"
            transition={easeSmooth}
            className="flex min-h-[calc(100dvh-var(--navbar-height)-80px)] items-center justify-center"
          >
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </motion.div>
        ) : (
          <motion.ol
            key={weekStartIso}
            variants={prefersReduced ? undefined : fadeVariants}
            initial={mounted ? "hidden" : false}
            animate="visible"
            transition={easeSmooth}
            className="flex flex-col"
          >
            {days.map((day, i) => {
              const iso = toIsoDate(day);
              const dayEntries = entriesByDay.get(iso) ?? [];
              const isToday = iso === todayIso;
              return (
                <li
                  key={iso}
                  className={cn(
                    "flex gap-10 py-5 @md:gap-20 md:py-7",
                    i > 0 && "border-t border-border",
                    dayEntries.length === 0 && "items-center"
                  )}
                >
                  <div className="flex shrink-0 flex-col">
                    <div
                      className={cn(
                        "text-[11px] font-semibold uppercase tracking-wider md:text-xs",
                        isToday ? "text-primary" : "text-muted-foreground"
                      )}
                    >
                      {weekdayFmt.format(day)}
                    </div>
                    <div
                      className={cn(
                        "mt-0.5 text-3xl font-semibold leading-none tracking-tight md:text-4xl",
                        isToday ? "text-primary" : "text-foreground"
                      )}
                    >
                      {day.getUTCDate()}
                    </div>
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-4">
                    {dayEntries.length > 0 ? (
                      <div className="flex flex-row flex-wrap gap-3 sm:contents">
                        <AnimatePresence mode="popLayout">
                          {dayEntries.map((entry) => (
                            <MealCard
                              key={entry.id}
                              entry={entry}
                              onRemove={() => removeEntry(entry.id, iso)}
                              unplanLabel={t("unplan")}
                              reducedMotion={!!prefersReduced}
                            />
                          ))}
                        </AnimatePresence>
                        <motion.button
                          layout={!prefersReduced}
                          transition={springSnappy}
                          type="button"
                          onClick={() => onOpenPlanSheet(iso)}
                          className="flex w-[calc(50%-6px)] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-input-border bg-input-bg text-sm text-foreground aspect-square transition-colors hover:bg-secondary-dark sm:w-36 lg:w-40"
                        >
                          <Plus className="size-5" aria-hidden />
                          {t("planAnother")}
                        </motion.button>
                      </div>
                    ) : (
                      <motion.button
                        layout={!prefersReduced}
                        transition={springSnappy}
                        type="button"
                        onClick={() => onOpenPlanSheet(iso)}
                        className="inline-flex cursor-pointer items-center gap-2 self-start rounded-lg border border-dashed border-input-border bg-input-bg px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-secondary-dark sm:self-center"
                      >
                        <Plus className="size-3.5" aria-hidden />
                        {t("planMeal")}
                      </motion.button>
                    )}
                  </div>
                </li>
              );
            })}
          </motion.ol>
        )}
      </AnimatePresence>
    </div>
  );
}
